import { useReducer, Reducer, ChangeEvent, useEffect, useMemo } from "react";
import { maxLength, minLength, TextInput } from "react-admin";
import "../../common/styles/TwoInputsInRow.css";
import { required } from "react-admin";
import { useForm } from "react-final-form";
import { InputAdornment, CircularProgress } from "@material-ui/core";
import { debounce } from "lodash";
import Axios, { AxiosResponse } from "axios";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { onlyNumbersAndCapitalLetters } from "./validations";

interface MakeModelSelectorProps {
  variant?: "standard" | "filled" | "outlined";
}

interface IVehicleState {
  loading: boolean;
  make: string;
  model: string;
}

interface NTHSAResult {
  Value: string | null;
  Variable: string;
}

interface NTHSAResponse {
  Count: number;
  Message: string;
  SearchCriteria: string;
  Results: Array<NTHSAResult>;
}

const SET_LOADING = "SET_LOADING";
const SET_VEHICLE = "SET_VEHICLE";

interface IVehicleStateAction {
  type: string;
  payload: boolean | { make?: string; model?: string; trim?: string };
}

const decodeVin = async (vin: string) => {
  if (!vin || vin.length < 1) {
    return {};
  }

  const url = `https://vpic.nhtsa.dot.gov/api/vehicles/decodevin/${vin}?format=json`;
  try {
    const response = (await Axios.get(url)) as AxiosResponse<NTHSAResponse>;
    const make = response.data.Results.find((x) => x.Variable === "Make")
      ?.Value;
    if (!make) {
      return {};
    }

    let model = response.data.Results.find((x) => x.Variable === "Model")
      ?.Value;
    if (!model) {
      return { make };
    }

    const trim = response.data.Results.find((x) => x.Variable === "Trim")
      ?.Value;
    if (!trim) {
      return { make, model };
    }

    model = model + " " + trim;
    return { make, model };
  } catch {
    return {};
  }
};

const isBoolean = (
  payload: boolean | { make?: string; model?: string }
): payload is boolean => {
  return typeof payload === "boolean";
};

export const MakeModelSelector = ({ variant }: MakeModelSelectorProps) => {
  const form = useForm();

  useEffect(() => {
    if (vehicleState.make) {
      form.change("make", vehicleState.make);
    }

    if (vehicleState.model) {
      form.change("model", vehicleState.model);
    }
  });

  const vehicleStateReducer = (
    prevState: IVehicleState,
    action: IVehicleStateAction
  ) => {
    switch (action.type) {
      case SET_LOADING:
        if (isBoolean(action.payload)) {
          return {
            ...prevState,
            loading: action.payload,
          };
        }
        break;
      case SET_VEHICLE:
        if (!isBoolean(action.payload)) {
          return {
            ...prevState,
            make: prevState.make ?? "",
            model: prevState.model ?? "",
            ...action.payload,
          };
        }

        break;
    }
    return prevState;
  };

  const [vehicleState, dispatchVehicleState] = useReducer<
    Reducer<IVehicleState, IVehicleStateAction>
  >(vehicleStateReducer, { loading: false, make: "", model: "" });

  const debouncedMethod = useMemo(
    () =>
      debounce(async (nextValue) => {
        const retVal = await decodeVin(nextValue);
        dispatchVehicleState({ type: SET_LOADING, payload: false });
        dispatchVehicleState({ type: SET_VEHICLE, payload: retVal });
      }, 3000),
    []
  );

  return (
    <>
      <ProtectedElement source="vin">
        <TextInput
          variant={variant}
          source="vin"
          label="resources.admin-vehicles.labels.vin"
          validate={[
            required(),
            minLength(17),
            maxLength(17),
            onlyNumbersAndCapitalLetters("messages.Vehicle.InvalidVinFormat"),
          ]}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            dispatchVehicleState({
              type: SET_LOADING,
              payload: e.target.value.length > 0,
            });
            debouncedMethod(e.target.value);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {vehicleState.loading && (
                  <CircularProgress style={{ width: "32px", height: "32px" }} />
                )}
              </InputAdornment>
            ),
          }}
        />
      </ProtectedElement>

      <div className="two-inputs-container">
        <ProtectedElement source="make">
          <TextInput
            variant={variant}
            source="make"
            label="resources.admin-vehicles.labels.make"
          />
        </ProtectedElement>
        <ProtectedElement source="model">
          <TextInput
            variant={variant}
            source="model"
            label="resources.admin-vehicles.labels.model"
          />
        </ProtectedElement>
      </div>
    </>
  );
};
